/**
 * Service worker registration + silent auto-update.
 *
 * Ported from muster-trail's lib/pwa.ts, which inherited it from the Compass
 * portal. The shape: check for a new worker whenever the app is foregrounded,
 * and once the new worker takes control, reload INTO it so the fresh code runs.
 * Nobody sees a "new version available" nag and nobody reinstalls anything.
 *
 * Two guards that trail paid for the hard way, kept verbatim in spirit:
 *
 * 1. First-install guard, and `controlled` MUST be a live flag rather than a
 *    snapshot taken at load. A snapshot is wrong for exactly the tab we care
 *    about: a first visit loads uncontrolled, so a frozen `false` would suppress
 *    not just the install reload but every later one, and a tab left open across
 *    a deploy would never update again. That is the precise failure this worker
 *    exists to prevent.
 *
 * 2. A generic busy check, so a reload never yanks a half-filled form away.
 *    Anything marked [data-pwa-busy] (Premise puts that on an open Sheet), or a
 *    focused input with something typed in it. A deferred reload retries on the
 *    next foreground.
 *
 * Premise is hash-routed, so a reload returns to the same screen. trail had no
 * router and its silent reload dumped reps back on Home, which took a
 * sessionStorage screen-restore to fix (reference-trail-dev-gotchas). We do not
 * need that here, but the busy guard still matters: sheet state is in memory.
 */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  // Dev serves no sw.js (the emitting plugin is build-only), and a worker would
  // shadow Vite's HMR anyway.
  if (!import.meta.env.PROD) return;

  // Live flag, not a snapshot. False here means this page load has no worker
  // yet, so its first controllerchange is an install, not an update.
  let controlled = Boolean(navigator.serviceWorker.controller);

  let pending = false;
  let refreshing = false;

  function busy(): boolean {
    if (document.querySelector('[data-pwa-busy]')) return true;
    const el = document.activeElement;
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      if (el.value) return true;
    }
    return false;
  }

  function maybeReload(): void {
    if (refreshing || !pending || busy()) return;
    refreshing = true;
    window.location.reload();
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!controlled) {
      controlled = true; // first install claiming this page: nothing to reload for
      return;
    }
    pending = true;
    maybeReload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'visible') return;
          reg.update().catch(() => {}); // pick up a deploy while the app stayed open
          maybeReload(); // retry a reload deferred because a sheet was open
        });
      })
      .catch(() => {});
  });
}

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: InstallPromptEvent | null = null;
const promptListeners = new Set<() => void>();
const emit = () => promptListeners.forEach((l) => l());

/**
 * Start listening for `beforeinstallprompt` at BOOT, before React renders.
 * Never from a component.
 *
 * Chrome fires the event once, a moment after page load, while the owner is
 * still on the sign-in screen. A listener living in a component's useEffect
 * attaches long after the event has come and gone, and Chrome never fires it
 * again for that page load. That is exactly how trail shipped an install offer
 * that could never appear (PR #70). Capture at boot, stash it, and let the card
 * read it whenever it mounts.
 */
export function captureInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // suppress Chrome's mini-infobar; we choose the moment
    deferredPrompt = e as InstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

/** The stashed prompt, or null. Stable reference, safe for useSyncExternalStore. */
export function getInstallPrompt(): InstallPromptEvent | null {
  return deferredPrompt;
}

export function subscribeInstallPrompt(fn: () => void): () => void {
  promptListeners.add(fn);
  return () => void promptListeners.delete(fn);
}

/** Replay the stashed prompt on a real user gesture. It is one-shot. */
export async function runInstallPrompt(): Promise<void> {
  const p = deferredPrompt;
  if (!p) return;
  await p.prompt();
  await p.userChoice;
  deferredPrompt = null;
  emit();
}

/** True when running as an installed app rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports as a Mac; the touch check disambiguates.
    (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  );
}
